import api from "@/utils/api";

/**
 * 用户相关接口
 */

/**
 * 发送验证码
 * @param {string} mobile - 手机号码
 * @param {string} event - 事件类型 (register, changemobile, changepwd, resetpwd)
 * @returns {Promise}
 */
export function sendSmsCode(mobile, event = "register") {
  const formData = new FormData();
  formData.append("mobile", mobile);
  formData.append("event", event);
  return api.post("/sms/send", formData);
}

/**
 * 用户登录
 * @param {Object} params - 登录参数
 * @param {string} params.account - 手机号码或邮箱
 * @param {string} params.password - 密码
 * @returns {Promise}
 */
export function login(params) {
  const formData = new FormData();
  formData.append("account", params.account);
  formData.append("password", params.password);
  return api.post("/user/login", formData);
}

/**
 * 用户注册
 * @param {Object} params - 注册参数
 * @param {string} params.mobile - 手机号
 * @param {string} params.email - 邮箱
 * @param {string} params.password - 密码
 * @param {string} params.code - 验证码
 * @param {string} params.invite_code - 邀请码
 * @returns {Promise}
 */
export function register(params) {
  const formData = new FormData();
  formData.append("mobile", params.mobile);
  formData.append("email", params.email);
  formData.append("password", params.password);
  formData.append("code", params.code);
  formData.append("invite_code", params.invite_code);
  return api.post("/user/register", formData);
}

/**
 * 获取文本信息
 * @param {string} type - 文本类型 (1=关于我们, 2=邮箱授权, 3=用户协议, 4=隐私政策)
 * @returns {Promise}
 */
export function getText(type) {
  return api.get("/user/getText", {
    params: { type },
  });
}

/**
 * 获取用户信息
 * @returns {Promise}
 */
export function getUserInfo() {
  return api.get("/user/getUserInfo");
}

/**
 * 获取推广列表
 * @returns {Promise}
 */
export function getPromotion(params = {}) {
  return api.get("/user/getPromotion", {
    params,
  });
}

/**
 * 修改个人信息
 * @param {Object} params - 个人信息参数
 * @param {string} params.mobile - 手机号码
 * @param {string} params.email - 邮箱
 * @param {string} params.avatar - 头像（文件路径或文件对象）
 * @param {string} params.wechat_username - 微信名
 * @param {string} params.wechat_no - 微信id
 * @param {string} params.alipay_username - 支付宝姓名
 * @param {string} params.alipay_account - 支付宝账号
 * @param {string} params.bank_name - 银行卡开户行
 * @param {string} params.bank_branch - 银行卡支行
 * @param {string} params.bank_account - 银行卡账号
 * @returns {Promise}
 */
export function updateProfile(params) {
  const formData = new FormData();
  formData.append("mobile", params.mobile);
  formData.append("email", params.email);
  if (params.avatar) {
    // 如果 avatar 是文件对象，直接添加；如果是字符串路径，也添加
    if (params.avatar instanceof File || params.avatar instanceof Blob) {
      formData.append("avatar", params.avatar);
    } else if (typeof params.avatar === "string") {
      formData.append("avatar", params.avatar);
    }
  }
  formData.append("wechat_username", params.wechat_username);
  formData.append("wechat_no", params.wechat_no);
  formData.append("alipay_username", params.alipay_username);
  formData.append("alipay_account", params.alipay_account);
  formData.append("bank_name", params.bank_name);
  formData.append("bank_branch", params.bank_branch);
  formData.append("bank_account", params.bank_account);
  return api.post("/user/profile", formData);
}

/**
 * 编辑邮箱授权码和笔名
 * @param {Object} params - 参数
 * @param {string} params.auth_code - 授权码
 * @param {string} params.pen_name - 笔名
 * @returns {Promise}
 */
export function changeEmailCode(params) {
  const formData = new FormData();
  formData.append("auth_code", params.auth_code);
  formData.append("pen_name", params.pen_name);
  return api.post("/user/changeEmailCode", formData);
}

export function checkSms(params) {
  return api.post("/sms/check", params);
}

/**
 * 重置密码
 * @param {Object} params - 重置密码参数
 * @param {string} params.mobile - 手机号码
 * @param {string} params.newpassword - 新密码
 * @param {string} params.captcha - 验证码
 * @returns {Promise}
 */
export function resetPassword(params) {
  const formData = new FormData();
  formData.append("mobile", params.mobile);
  formData.append("newpassword", params.newpassword);
  formData.append("captcha", params.captcha);
  return api.post("/user/resetpwd", formData);
}

/**
 * 修改密码
 * @param {Object} params - 修改密码参数
 * @param {string} params.oldpassword - 旧密码
 * @param {string} params.newpassword - 新密码
 * @returns {Promise}
 */
export function changePassword(params) {
  const formData = new FormData();
  formData.append("oldpassword", params.oldpassword);
  formData.append("newpassword", params.newpassword);
  return api.post("/user/changepwd", formData);
}

/**
 * 发件邮箱列表
 * @param {Object} params - 可选查询参数
 * @returns {Promise}
 */
export function getSenderEmailList(params = {}) {
  return api.get("/user_sender_email/index", {
    params,
  });
}

/**
 * 新增发件邮箱
 * @param {Object} params - 新增参数
 * @param {string} params.email - 邮箱
 * @param {string} params.auth_code - 授权码
 * @param {string} params.pen_name - 笔名
 * @param {string|number} params.is_default - 是否默认
 * @param {string|number} params.weigh - 权重
 * @returns {Promise}
 */
export function addSenderEmail(params) {
  const formData = new FormData();
  formData.append("email", params.email);
  formData.append("auth_code", params.auth_code);
  formData.append("pen_name", params.pen_name);
  formData.append("is_default", params.is_default);
  formData.append("weigh", params.weigh);
  return api.post("/user_sender_email/add", formData);
}

/**
 * 修改发件邮箱
 * @param {Object} params - 修改参数
 * @param {number|string} params.id - 记录 id
 * @param {string} params.email - 邮箱
 * @param {string} params.auth_code - 授权码（可空，表示不改）
 * @param {string} params.pen_name - 笔名
 * @param {string|number} params.is_default - 是否默认
 * @param {string|number} params.weigh - 权重
 * @returns {Promise}
 */
export function editSenderEmail(params) {
  const formData = new FormData();
  formData.append("id", params.id);
  formData.append("email", params.email);
  formData.append("auth_code", params.auth_code || "");
  formData.append("pen_name", params.pen_name);
  formData.append("is_default", params.is_default);
  formData.append("weigh", params.weigh);
  return api.post("/user_sender_email/edit", formData);
}

/**
 * 设置默认发件邮箱
 * @param {Object} params
 * @param {number|string} params.id - 记录 id
 * @returns {Promise}
 */
export function setDefaultSenderEmail(params) {
  const formData = new FormData();
  formData.append("id", params.id);
  return api.post("/user_sender_email/setDefault", formData);
}

/**
 * 删除发件邮箱
 * @param {Object} params
 * @param {number|string} params.id - 记录 id
 * @returns {Promise}
 */
export function deleteSenderEmail(params) {
  const formData = new FormData();
  formData.append("id", params.id);
  return api.post("/user_sender_email/del", formData);
}